import { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type PublicClip, ShortUrl } from '@air/api';
import { InlineInput, type InlineInputProps } from '@air/component-inline-input';
import {
  contentCollectionFormSelector,
  contentCollectionSessionIdSelector,
  updateContentCollectionClipsAction,
} from '@air/feature-content-collection/redux';
import { Button } from '@air/primitive-button';
import { useShortIdContext } from '@air/provider-short-id';

import { validateAssetTitle } from '~/components/AssetModalV2/utils';

export type ContentCollectionGalleryAssetTitleProps = {
  clip: PublicClip;
};

export const ContentCollectionGalleryAssetTitle = memo(({ clip }: ContentCollectionGalleryAssetTitleProps) => {
  const dispatch = useDispatch();
  const sessionId = useSelector(contentCollectionSessionIdSelector);
  const form = useSelector(contentCollectionFormSelector);
  const { shortId } = useShortIdContext();

  const [isEditing, setIsEditing] = useState(false);
  const defaultValue = clip.title;
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const onSubmit = useCallback<Required<InlineInputProps>['onSubmit']>(
    (newTitle) => {
      if (!sessionId) {
        return;
      }

      dispatch(
        updateContentCollectionClipsAction({
          clips: [
            {
              ...clip,
              title: newTitle ?? '',
            },
          ],
        }),
      );

      // biome-ignore lint/nursery/noFloatingPromises: PLA-1724 — pre-existing debt, not introduced by the color pairing
      ShortUrl.batchUpdateFormAssets(shortId, form.id, {
        formSessionId: sessionId,
        update: {
          title: !newTitle ? null : newTitle,
        },
        assetIds: [clip.assetId],
      });
    },
    [clip, dispatch, form.id, sessionId, shortId],
  );

  return (
    <div className="my-1">
      {isEditing ? (
        <InlineInput
          rows={1}
          allowNewLines={false}
          label="Asset name"
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the color pairing
          onSubmit={(value) => {
            setIsEditing(false);
            onSubmit(value);
          }}
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the color pairing
          onCancel={() => {
            setValue(defaultValue);
            setIsEditing(false);
          }}
          onChange={setValue}
          inputClassName="-ml-px pt-px px-1 text-16 md:text-16 font-medium h-7"
          defaultValue={value}
          validate={validateAssetTitle}
        />
      ) : (
        <Button
          appearance="ghost"
          color="gray"
          className="h-7 w-full justify-start px-1 font-medium ads:text-primary text-16 text-gray-12"
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the color pairing
          onClick={() => setIsEditing(true)}
        >
          <span className="truncate">{value || defaultValue}</span>
        </Button>
      )}
    </div>
  );
});

ContentCollectionGalleryAssetTitle.displayName = 'ContentCollectionGalleryAssetTitle';
